import React, { useEffect, useState, useMemo } from 'react';
import { Checkbox, FormControlLabel, Dialog, Typography, Box, Tooltip } from '@mui/material';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import {
  LinkManagementStatus,
  LinkManagementStatusMapper,
  LinkReviewCategory,
  LinkReviewCategoryMapper,
} from '../types';

interface IProps {
  paramKey: 'title' | 'status' | 'assetCategory';
  onChange: (value: any) => void;
}

const ColumnFilter = (props: IProps) => {
  const [titleOptions, setTitleOptions] = useState<{ title: string; value: string }[]>([]);

  const { paramKey, onChange } = props;

  useEffect(() => {
    if (paramKey === 'title') {
      // TODO: Fetch title options from an API
      setTitleOptions([
        { title: 'The Apothecary Diaries', value: 'The Apothecary Diaries' },
        { title: 'Solo Leveling', value: 'Solo Leveling' },
        { title: 'Demon Slayer', value: 'Demon Slayer' },
        { title: 'Attack On Titan', value: 'Attack On Titan' },
        //... Add more titles as needed
      ]);
    }
  }, [paramKey]);

  const title: string = useMemo(() => {
    switch (paramKey) {
      case 'title':
        return 'Title';
      case 'status':
        return 'Status';
      case 'assetCategory':
        return 'Category';
      default:
        return '';
    }
  }, [paramKey]);

  const options = useMemo(() => {
    switch (paramKey) {
      case 'title':
        return titleOptions.reduce(
          (acc: Record<string, string>, option) => ({
            ...acc,
            [option.title]: option.value,
          }),
          {},
        );
      case 'status':
        return LinkManagementStatus;
      case 'assetCategory':
        return LinkReviewCategory;
      default:
        return {};
    }
  }, [paramKey, titleOptions]);

  const filterMapper: Record<string, string> = useMemo(() => {
    switch (paramKey) {
      case 'title':
        return titleOptions.reduce(
          (acc: Record<string, string>, option) => ({
            ...acc,
            [option.value]: option.title,
          }),
          {},
        );
      case 'status':
        return LinkManagementStatusMapper;
      case 'assetCategory':
        return LinkReviewCategoryMapper;
      default:
        return {};
    }
  }, [paramKey, titleOptions]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, boolean>>(
    (Object.values(options) as string[]).reduce(
      (acc: Record<string, boolean>, item: string) => ({
        ...acc,
        [item]: false,
      }),
      {} as Record<string, boolean>,
    ),
  );

  useEffect(() => {
    setSelectedFilters(
      (Object.values(options) as string[]).reduce(
        (acc: Record<string, boolean>, item: string) => ({
          ...acc,
          [item]: false,
        }),
        {} as Record<string, boolean>,
      ),
    );
  }, [options]);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleCheckboxChange = (key: string) => {
    const newSelectedFilters = { ...selectedFilters, [key]: !selectedFilters[key] };
    setSelectedFilters(newSelectedFilters);

    const selectedParamValues = Object.entries(newSelectedFilters)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, isSelected]) => isSelected)
      .map(([key]) => key);
    onChange(selectedParamValues);
  };

  const handleClickAway = () => setMenuOpen(false);

  const isAnySelected = useMemo(
    () => Object.values(selectedFilters).some((value) => value),
    [selectedFilters],
  );

  const selectedFiltersText = useMemo(() => {
    return Object.keys(selectedFilters)
      .filter((key) => selectedFilters[key])
      .map((key) => filterMapper[key])
      .join(', ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  return (
    <>
      <Box display="flex" alignItems="center" gap="4px">
        <Tooltip title={selectedFiltersText}>
          <Typography
            sx={{
              backgroundColor: '#fff',
              borderRadius: '10px',
              fontSize: '14px',
              lineHeight: '26px',
              width: '250px',
              maxWidth: '250px',
              height: '35px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              border: '1px solid #E0E3E7',
              padding: '2px',
              paddingLeft: '5px',
            }}
          >
            {selectedFiltersText}
          </Typography>
        </Tooltip>
        <FilterAltIcon
          fontSize="small"
          sx={{
            color: isAnySelected ? '#FF4848' : '#44AEFB',
            cursor: 'pointer',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.2)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
          onClick={toggleMenu}
        />
      </Box>
      {menuOpen && (
        <Dialog
          open={menuOpen}
          onClose={handleClickAway}
          PaperProps={{
            style: {
              padding: '0px',
              minWidth: '200px',
              maxHeight: '50%',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 16px',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                lineHeight: '20px',
                color: '#A3A7B0',
              }}
            >
              {title}
            </Typography>
            <ArrowDropDownIcon
              fontSize="small"
              sx={{ color: '#A3A7B0', cursor: 'pointer' }}
              onClick={handleClickAway}
            />
          </Box>
          {Object.keys(selectedFilters).map((key) => (
            <FormControlLabel
              key={key}
              sx={{
                padding: '14px 16px',
                margin: '0px',
                '& .MuiFormControlLabel-label': {
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#A3A7B0',
                },
              }}
              control={
                <Checkbox
                  sx={{
                    padding: '0',
                    marginRight: '8px',
                  }}
                  checked={selectedFilters[key]}
                  onChange={() => handleCheckboxChange(key)}
                />
              }
              label={filterMapper[key]}
            />
          ))}
        </Dialog>
      )}
    </>
  );
};

export default ColumnFilter;
